import RevealWrapper from "../../base/RevealWrapper";
import { BUSINESS, HOURS } from "../../../mocks/siteData";

export interface FinalCTAContent {
  eyebrow: string;
  headline: string;
  subtext: string;
}

interface Props {
  content: FinalCTAContent;
}

const bgImage = "https://public.readdy.ai/ai/img_res/d3d8dda9eeab459fa0deb3ffead484ca.jpg";

export default function FinalCTA({ content }: Props) {
  const { eyebrow, headline, subtext } = content;

  return (
    <section id="contact" className="relative overflow-hidden">
      {/* Background image with strong overlay */}
      <div className="absolute inset-0">
        <img
          src={bgImage}
          alt="Book an appointment at The Lash Bar"
          className="w-full h-full object-cover object-center"
        />
        <div className="absolute inset-0 bg-charcoal-dark/88" />
        <div className="absolute inset-0 bg-gradient-to-r from-charcoal-dark via-charcoal-dark/95 to-charcoal-dark/80" />
      </div>

      {/* Content */}
      <div className="relative max-w-7xl mx-auto px-6 md:px-10 py-24 md:py-32">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-start">
          {/* Left: Main CTA */}
          <RevealWrapper direction="left">
            <div>
              <span className="section-eyebrow text-gold">{eyebrow}</span>
              <span className="block w-12 h-px bg-gold my-5" />
              <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl font-light text-white leading-tight mt-0 mb-6">
                {headline}
              </h2>
              <p className="font-sans text-sm text-white/50 leading-relaxed mb-10 max-w-md font-light">
                {subtext}
              </p>

              <div className="flex flex-col sm:flex-row gap-4 mb-10">
                <a
                  href={BUSINESS.bookingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-gold text-sm px-10 py-5"
                >
                  Reserve Your Appointment
                  <i className="ri-arrow-right-line" />
                </a>
                <a href={BUSINESS.phoneTel} className="btn-outline text-sm px-10 py-5">
                  <i className="ri-phone-line" />
                  {BUSINESS.phone}
                </a>
              </div>

              {/* Contact details */}
              <div className="space-y-3">
                <div className="flex items-start gap-3">
                  <i className="ri-map-pin-line text-gold text-sm mt-0.5 flex-shrink-0" />
                  <a
                    href={BUSINESS.directionsUrl}
                    target="_blank"
                    rel="nofollow noopener noreferrer"
                    className="font-sans text-sm text-white/50 hover:text-white/70 transition-colors cursor-pointer font-light"
                  >
                    {BUSINESS.address}<br />{BUSINESS.city}, {BUSINESS.state} {BUSINESS.zip}
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <i className="ri-phone-line text-gold text-sm flex-shrink-0" />
                  <a href={BUSINESS.phoneTel} className="font-sans text-sm text-white/50 hover:text-white/70 transition-colors font-light">
                    {BUSINESS.phone}
                  </a>
                </div>
              </div>
            </div>
          </RevealWrapper>

          {/* Right: Hours + Map */}
          <RevealWrapper direction="right" delay={0.2}>
            <div>
              {/* Hours */}
              <div className="border border-white/10 p-7 mb-5">
                <div className="flex items-center gap-3 mb-5">
                  <i className="ri-time-line text-gold" />
                  <h3 className="font-serif text-lg font-light text-white">Business Hours</h3>
                </div>
                <ul className="space-y-2.5">
                  {HOURS.map((h) => (
                    <li key={h.day} className="flex justify-between items-center">
                      <span className="font-sans text-xs text-white/35">{h.day}</span>
                      <span className="font-sans text-xs text-white/65 font-medium">{h.hours}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Map */}
              <div className="overflow-hidden" style={{ height: '220px' }}>
                <iframe
                  width="100%"
                  height="220"
                  frameBorder="0"
                  src={BUSINESS.mapEmbed}
                  allowFullScreen
                  title="The Lash Bar location map"
                  aria-label="Google Map showing The Lash Bar at 3397 N 1200 E Suite 110, Lehi, UT 84043"
                />
              </div>
            </div>
          </RevealWrapper>
        </div>
      </div>
    </section>
  );
}
